// src/App.jsx
import { HashRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layout
import Layout from './components/layout/Layout';

// Pages
import Dashboard from './pages/Dashboard'; // (kept, though not used as root)

// Students
import StudentList from './pages/Students/StudentList';
import StudentNew from './pages/Students/StudentNew';
import StudentDetails from './pages/Students/StudentDetails';
import StudentEdit from './pages/Students/StudentEdit';

// Subjects
import SubjectList from './pages/Subjects/SubjectList';
import SubjectNew from './pages/Subjects/SubjectNew';
import SubjectEdit from './pages/Subjects/SubjectEdit';

// Intakes
import IntakeList from './pages/Intakes/IntakeList';
import IntakeNew from './pages/Intakes/IntakeNew';
import IntakeEdit from './pages/Intakes/IntakeEdit';

// Reports
import ReportIntake from './pages/Reports/ReportIntake'; 
import ReportExamRegistrations from './pages/Reports/ReportExamRegistrations';
import ReportRismMembership from './pages/Reports/ReportRismMembership';
import ReportMissingSubjects from './pages/Reports/ReportMissingSubjects';

// Exam Sessions
import ExamSessionList from './pages/ExamSessions/ExamSessionList';
import ExamSessionNew from './pages/ExamSessions/ExamSessionNew';
import ExamSessionEdit from './pages/ExamSessions/ExamSessionEdit';

// Enrollments
import EnrollmentList from './pages/Enrollments/EnrollmentList';
import EnrollmentNew from './pages/Enrollments/EnrollmentNew';
import EnrollmentEdit from './pages/Enrollments/EnrollmentEdit';

// Payments
import PaymentList from './pages/Payments/PaymentList';
import PaymentDetails from './pages/Payments/PaymentDetails';
import PaymentNew from './pages/Payments/PaymentNew';
import PaymentEdit from './pages/Payments/PaymentEdit';

// Exam Registrations
import ExamRegistrationList from './pages/Exams/ExamRegistrationList';
import ExamRegistrationNew from './pages/Exams/ExamRegistrationNew';
import ExamRegistrationEdit from './pages/Exams/ExamRegistrationEdit';
import ExamRegistrationDetails from './pages/Exams/ExamRegistrationDetails';
import StudentExamRegistrationNew from './pages/Exams/StudentExamRegistrationNew';

// Admin
import PaymentsAdmin from './pages/Admin/PaymentsAdmin';
import EnrollmentsAdmin from './pages/Admin/EnrollmentsAdmin';
import StudentsAdmin from './pages/Admin/StudentsAdmin';
import ExamRegistrationsAdmin from './pages/Admin/ExamRegistrationsAdmin';

// Auth
import RequireAuth from './components/auth/RequireAuth'; // <-- new
import Login from './pages/Login'; // <-- new

const queryClient = new QueryClient();

// Small helper to avoid repeating wrappers
function Protected({ children }) {
  return (
    <RequireAuth>
      <Layout>{children}</Layout>
    </RequireAuth>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <Routes>
          {/* Public route: Login */}
          <Route path="/login" element={<Login />} />

          {/* Root now shows the Intake/Subject Dashboard (protected) */}
          <Route path="/" element={<Protected><Dashboard /></Protected>} />

          {/* Students */}
          <Route path="/students" element={<Protected><StudentList /></Protected>} />
          <Route path="/students/new" element={<Protected><StudentNew /></Protected>} />
          <Route path="/students/:id" element={<Protected><StudentDetails /></Protected>} />
          <Route path="/students/:id/edit" element={<Protected><StudentEdit /></Protected>} />

          {/* Reports */}
          <Route path="/report-intake" element={<Protected><ReportIntake /></Protected>} />
          <Route path="/report-exam-registration" element={<Protected><ReportExamRegistrations /></Protected>} /> 
          <Route path="/report-rism-membership" element={<Protected><ReportRismMembership /></Protected>} />
          <Route path="/report-missing-subjects" element={<Protected><ReportMissingSubjects /></Protected>} />

          {/* Subjects */}
          <Route path="/subjects" element={<Protected><SubjectList /></Protected>} />
          <Route path="/subjects/new" element={<Protected><SubjectNew /></Protected>} />
          <Route path="/subjects/:id/edit" element={<Protected><SubjectEdit /></Protected>} />

          {/* Intakes */}
          <Route path="/intakes" element={<Protected><IntakeList /></Protected>} />
          <Route path="/intakes/new" element={<Protected><IntakeNew /></Protected>} />
          <Route path="/intakes/:id/edit" element={<Protected><IntakeEdit /></Protected>} />

          {/* Exam Sessions */}
          <Route path="/exam-sessions" element={<Protected><ExamSessionList /></Protected>} />
          <Route path="/exam-sessions/new" element={<Protected><ExamSessionNew /></Protected>} />
          <Route path="/exam-sessions/:id/edit" element={<Protected><ExamSessionEdit /></Protected>} />

          {/* Exam Registrations */}
          <Route path="/exam-registrations" element={<Protected><ExamRegistrationList /></Protected>} />
          <Route path="/exam-registrations/new" element={<Protected><ExamRegistrationNew /></Protected>} />
          <Route path="/exam-registrations/:id" element={<Protected><ExamRegistrationDetails /></Protected>} />
          <Route path="/exam-registrations/:id/edit" element={<Protected><ExamRegistrationEdit /></Protected>} />
          <Route path="/students/:studentId/exams/new" element={<Protected><StudentExamRegistrationNew /></Protected>} />

          {/* Enrollments */}
          <Route path="/enrollments" element={<Protected><EnrollmentList /></Protected>} />
          <Route path="/students/:id/enrollments/new" element={<Protected><EnrollmentNew /></Protected>} />
          <Route path="/enrollments/new" element={<Protected><EnrollmentNew /></Protected>} />
          <Route path="/enrollments/:id/edit" element={<Protected><EnrollmentEdit /></Protected>} />

          {/* Payments */}
          <Route path="/payments" element={<Protected><PaymentList /></Protected>} />
          <Route path="/students/:id/payments/new" element={<Protected><PaymentNew /></Protected>} />
          <Route path="/payments/new" element={<Protected><PaymentNew /></Protected>} />
          <Route path="/payments/:id" element={<Protected><PaymentDetails /></Protected>} />
          <Route path="/payments/:id/edit" element={<Protected><PaymentEdit /></Protected>} />

          {/* Admin */}
          <Route path="/admin/payments-archive" element={<Protected><PaymentsAdmin /></Protected>} />
          <Route path="/admin/enrollments" element={<Protected><EnrollmentsAdmin /></Protected>} />
          <Route path="/admin/students" element={<Protected><StudentsAdmin /></Protected>} />
          <Route path="/admin/exam-registrations" element={<Protected><ExamRegistrationsAdmin /></Protected>} />


          {/* Stubs / Coming soon */}

          <Route
            path="/reports/student-progress"
            element={
              <Protected>
                <div>Student Progress Report (Coming Soon)</div>
              </Protected>
            }
          />
          <Route
            path="/settings"
            element={
              <Protected>
                <div>Settings Page (Coming Soon)</div>
              </Protected>
            }
          />
        </Routes>
      </HashRouter>
    </QueryClientProvider>
  );
}

export default App;
